import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Sparkles, RefreshCw, Check, X, Trash2, Mail, MapPin,
  Building2, Calendar, ArrowLeft, Inbox
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function DeclinationsReview() {
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [mailbox, setMailbox] = useState('shared');
  const [edits, setEdits] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([
        axios.get(`${API}/declinations/parser/queue?status=pending`, authHeaders()),
        axios.get(`${API}/declinations/parser/queue/counts`, authHeaders()),
      ]);
      setItems(q.data.items || []);
      setGrouped(q.data.grouped || []);
      setCounts(c.data || {});
    } catch (e) {
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const viewEmail = async (item) => {
    try {
      const res = await axios.get(`${API}/declinations/parser/queue/${item.id}/email`, { ...authHeaders(), responseType: 'text' });
      const w = window.open('', '_blank');
      if (w) {
        // Render untrusted email HTML inside a sandboxed iframe (no scripts, opaque
        // origin) so it can never touch this app's session/localStorage.
        const srcdoc = res.data.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        w.document.open();
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Email source</title><style>html,body{margin:0;height:100%}iframe{border:0;width:100%;height:100%}</style></head><body><iframe sandbox="" srcdoc="${srcdoc}"></iframe></body></html>`);
        w.document.close();
      }
    } catch (e) {
      toast.error('Could not load the email');
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/declinations/parser/scan`,
        { mailbox, max_results: 50 }, authHeaders());
      const d = res.data;
      toast.success(`Scanned ${d.scanned} emails · ${d.new_items} new referral(s) queued${d.skipped_existing ? ` · ${d.skipped_existing} already processed` : ''}`);
      loadQueue();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Scan failed';
      toast.error(detail);
    } finally {
      setScanning(false);
    }
  };

  const fieldVal = (item, key) => (edits[item.id]?.[key] ?? item[key] ?? '');

  const setField = (id, key, value) => {
    setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));
  };

  const persistEdits = async (id) => {
    const payload = edits[id];
    if (!payload || Object.keys(payload).length === 0) return;
    try {
      await axios.put(`${API}/declinations/parser/queue/${id}`, payload, authHeaders());
    } catch (e) { /* non-blocking */ }
  };

  const handleApprove = async (item) => {
    setBusyId(item.id);
    try {
      await persistEdits(item.id);
      const res = await axios.post(`${API}/declinations/parser/queue/${item.id}/approve`, {}, authHeaders());
      toast.success(res.data.geocoded ? 'Approved & mapped' : 'Approved (no coordinates found)');
      setItems(prev => prev.filter(i => i.id !== item.id));
      setCounts(c => ({ ...c, pending: Math.max(0, c.pending - 1), approved: c.approved + 1 }));
    } catch (e) {
      toast.error('Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const handleApproveAll = async () => {
    if (items.length === 0) { toast.warning('No pending referrals to approve.'); return; }
    if (!window.confirm(`Approve and map all ${items.length} pending referral(s)? This runs in batches and may take a minute.`)) return;
    setApprovingAll(true);
    const BATCH = 25;
    let totalApproved = 0, totalGeocoded = 0, totalSkipped = 0;
    let safety = 0;
    try {
      while (true) {
        safety += 1;
        if (safety > 200) break; // hard stop guard against an unexpected loop
        const res = await axios.post(
          `${API}/declinations/parser/queue/approve-all?limit=${BATCH}`,
          {},
          { ...authHeaders(), timeout: 120000 }
        );
        const d = res.data || {};
        totalApproved += d.approved || 0;
        totalGeocoded += d.geocoded || 0;
        totalSkipped += d.skipped || 0;
        const remaining = d.remaining || 0;
        // No progress but items remain -> avoid an infinite loop
        if ((d.approved || 0) === 0 && remaining > 0) break;
        if (remaining > 0) {
          toast.message(`Approved ${totalApproved} so far · ${remaining} remaining…`);
        } else {
          break;
        }
      }
      toast.success(`Approved ${totalApproved} referral(s) · ${totalGeocoded} mapped${totalSkipped ? ` · ${totalSkipped} skipped` : ''}`);
      loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Bulk approve paused after ${totalApproved} approved. Click Approve All again to continue.`);
      loadQueue();
    } finally {
      setApprovingAll(false);
    }
  };

  const handleReject = async (item) => {
    setBusyId(item.id);
    try {
      await axios.post(`${API}/declinations/parser/queue/${item.id}/reject`, {}, authHeaders());
      toast.success('Rejected');
      setItems(prev => prev.filter(i => i.id !== item.id));
      setCounts(c => ({ ...c, pending: Math.max(0, c.pending - 1), rejected: c.rejected + 1 }));
    } catch (e) {
      toast.error('Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="declinations-review-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Link to="/admin/declinations-map" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft size={18} />
            </Link>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Sparkles className="text-amber-500" size={26} /> Declined Referrals — AI Parser
            </h1>
          </div>
          <p className="text-muted-foreground mt-1 ml-7">
            Automatically scans mail sent to <span className="font-semibold">declinations@elitehomerehab.com</span> every 15 minutes. Review the extracted data, then approve to add to the map.
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-sm" data-testid="count-pending">Pending: {counts.pending}</Badge>
          <Badge variant="outline" className="text-sm text-green-600" data-testid="count-approved">Approved: {counts.approved}</Badge>
          <Badge variant="outline" className="text-sm text-red-500" data-testid="count-rejected">Rejected: {counts.rejected}</Badge>
        </div>
      </div>

      {/* Scan controls */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-muted-foreground">Monitored Address</label>
            <Input value="declinations@elitehomerehab.com" readOnly disabled data-testid="scan-address-input" />
          </div>
          <div className="min-w-[160px]">
            <label className="text-xs font-medium text-muted-foreground">Mailbox</label>
            <select
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={mailbox}
              onChange={(e) => setMailbox(e.target.value)}
              data-testid="scan-mailbox-select"
            >
              <option value="shared">Shared Admin Inbox</option>
              <option value="personal">My Work Email</option>
            </select>
          </div>
          <Button onClick={handleScan} disabled={scanning} data-testid="scan-emails-btn">
            <RefreshCw size={16} className={`mr-1 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning…' : 'Scan Emails'}
          </Button>
          <Button variant="outline" onClick={loadQueue} disabled={loading} data-testid="refresh-queue-btn">
            <Inbox size={16} className="mr-1" /> Refresh
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          New emails to <span className="font-semibold">declinations@elitehomerehab.com</span> are parsed automatically. Use "Scan Emails" to pull them in immediately. Already-processed emails are skipped.
        </p>
      </Card>

      {/* Grouped summary */}
      {grouped.length > 0 && (
        <Card className="p-4" data-testid="grouped-summary">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="text-lg font-semibold">Pending by City + Agency</h2>
            <Link to="/admin/declinations-map" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="hiring-demand-link">
              <MapPin size={14} /> View hiring demand by ZIP / territory →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {grouped.map((g, idx) => (
              <div key={idx} className="border rounded-lg p-3 bg-muted/30" data-testid={`group-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium flex items-center gap-1"><MapPin size={14} /> {g.city}</span>
                  <Badge>{g.count}</Badge>
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <Building2 size={13} /> {g.agency}
                </div>
                {Object.keys(g.disciplines || {}).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {Object.entries(g.disciplines).map(([d, n]) => (
                      <Badge key={d} variant="outline" className="text-xs">{d}: {n}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Review queue */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold">Review Queue ({items.length})</h2>
          {items.length > 0 && (
            <Button
              size="sm"
              onClick={handleApproveAll}
              disabled={approvingAll}
              data-testid="decl-approve-all-btn"
            >
              <Check size={15} className="mr-1" />
              {approvingAll ? 'Approving…' : `Approve All (${items.length})`}
            </Button>
          )}
        </div>
        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground" data-testid="empty-queue">
            <Mail className="mx-auto mb-2 opacity-50" size={32} />
            No pending referrals. Scan your mailbox to pull in declined-referral emails.
          </Card>
        ) : (
          items.map((item) => (
            <Card key={item.id} className="p-4" data-testid={`queue-item-${item.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{item.subject || '(no subject)'}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    From {item.from_email || 'unknown'}{item.email_date ? ` · ${new Date(item.email_date).toLocaleDateString()}` : ''}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => viewEmail(item)} data-testid={`view-email-${item.id}`}>
                    <Mail size={15} className="mr-1" /> View Email
                  </Button>
                  <Button size="sm" onClick={() => handleApprove(item)} disabled={busyId === item.id} data-testid={`approve-${item.id}`}>
                    <Check size={15} className="mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleReject(item)} disabled={busyId === item.id} data-testid={`reject-${item.id}`}>
                    <X size={15} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ['discipline', 'Discipline'],
                  ['agency', 'Agency / Source'],
                  ['date', 'Date'],
                  ['city', 'City'],
                  ['state', 'State'],
                  ['zip_code', 'ZIP'],
                  ['address', 'Address'],
                  ['reason', 'Reason'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="text-xs font-medium text-muted-foreground">{label}</label>
                    <Input
                      value={fieldVal(item, key)}
                      onChange={(e) => setField(item.id, key, e.target.value)}
                      onBlur={() => persistEdits(item.id)}
                      placeholder="—"
                      data-testid={`field-${key}-${item.id}`}
                    />
                  </div>
                ))}
              </div>

              {item.snippet && (
                <p className="text-xs text-muted-foreground mt-3 italic line-clamp-2">"{item.snippet}"</p>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
